import { useMemo, useState } from "react";

import { tickToPrice } from "../views/liquidity/types";

interface TickBucket {
  tick: number;
  liquidity: number;
}

interface LiquidityHistogramProps {
  /** Liquidity data per tick bucket */
  buckets: TickBucket[];
  /** Current active tick */
  currentTick: number;
  /** User position lower tick (optional) */
  positionTickLower?: number;
  /** User position upper tick (optional) */
  positionTickUpper?: number;
  /** Height in px */
  height?: number;
  /** Pool's active liquidity — used as fallback when all buckets are 0 */
  poolLiquidity?: number;
  /** Token0 symbol for price display */
  token0Symbol?: string;
  /** Token1 symbol for price display */
  token1Symbol?: string;
  /** Token0 decimals for price conversion */
  decimals0?: number;
  /** Token1 decimals for price conversion */
  decimals1?: number;
}

function formatTick(tick: number): string {
  const abs = Math.abs(tick);
  const sign = tick < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return String(tick);
}

function formatLiq(v: number): string {
  if (v >= 1e18) return `${(v / 1e18).toFixed(1)}E`;
  if (v >= 1e15) return `${(v / 1e15).toFixed(1)}P`;
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

const GOLD = "rgba(201,168,76,";
const ACCENT = "rgba(79,143,234,";

export function LiquidityHistogram({
  buckets,
  currentTick,
  positionTickLower,
  positionTickUpper,
  height = 180,
  poolLiquidity,
  token0Symbol,
  token1Symbol,
  decimals0,
  decimals1,
}: LiquidityHistogramProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const hasPriceData = decimals0 !== undefined && decimals1 !== undefined;

  const data = useMemo(() => {
    if (buckets.length === 0) return null;

    // If all zero but poolLiquidity provided, show flat bar
    const allZero = buckets.every((b) => b.liquidity === 0);
    const display =
      allZero && poolLiquidity && poolLiquidity > 0
        ? buckets.map((b) => ({ ...b, liquidity: poolLiquidity }))
        : buckets;

    const maxLiq = Math.max(...display.map((b) => b.liquidity));
    if (maxLiq === 0) return null;

    return { display, maxLiq };
  }, [buckets, poolLiquidity]);

  if (!data) {
    return (
      <p className="py-4 text-center text-xs text-[var(--color-text-muted)]">
        No liquidity data available
      </p>
    );
  }

  const { display, maxLiq } = data;
  const n = display.length;

  // Layout constants
  const ml = 58; // left margin for Y labels
  const mr = 12; // right margin
  const mt = 8; // top margin
  const mb = 28; // bottom margin for X labels
  const w = 600; // viewBox width (scales with container)
  const chartW = w - ml - mr;
  const chartH = height - mt - mb;
  const barGap = 1;
  const barW = Math.max(1, (chartW - barGap * (n - 1)) / n);

  // Y axis ticks (4 evenly spaced)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    value: maxLiq * f,
    y: mt + chartH * (1 - f),
  }));

  // Find the current tick bar index for reference line
  const currentIdx = display.findIndex(
    (b, i) =>
      b.tick <= currentTick &&
      (i === display.length - 1 || (display[i + 1]?.tick ?? 0) > currentTick),
  );

  // Hovered bucket
  const hoveredBucket = hovered !== null ? display[hovered] : null;

  function formatXLabel(tick: number): string {
    if (hasPriceData) {
      return tickToPrice(tick, decimals0, decimals1);
    }
    return formatTick(tick);
  }

  return (
    <div className="relative" style={{ width: "100%", height }}>
      <svg
        viewBox={`0 0 ${String(w)} ${String(height)}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: "100%" }}
        onMouseLeave={() => {
          setHovered(null);
        }}
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="liq-bar-default" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(79,143,234,0.5)" />
            <stop offset="100%" stopColor="rgba(79,143,234,0.15)" />
          </linearGradient>
          <linearGradient id="liq-bar-in-range" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(45,212,191,0.6)" />
            <stop offset="100%" stopColor="rgba(45,212,191,0.15)" />
          </linearGradient>
          <linearGradient id="liq-bar-current" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(201,168,76,0.8)" />
            <stop offset="100%" stopColor="rgba(201,168,76,0.3)" />
          </linearGradient>
        </defs>

        {/* Y-axis label */}
        <text
          x={4}
          y={mt + 4}
          fontSize={7}
          fontFamily="monospace"
          fill={`${GOLD}0.35)`}
          letterSpacing="0.05em"
        >
          Liquidity
        </text>

        {/* Grid lines */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={ml}
              y1={t.y}
              x2={w - mr}
              y2={t.y}
              stroke={`${GOLD}0.06)`}
              strokeWidth={0.5}
              strokeDasharray={i === 0 ? "none" : "2 3"}
            />
            <text
              x={ml - 6}
              y={t.y + 3}
              textAnchor="end"
              fontSize={9}
              fontFamily="monospace"
              fill={`${GOLD}0.4)`}
            >
              {formatLiq(t.value)}
            </text>
          </g>
        ))}

        {/* Position range highlight */}
        {positionTickLower !== undefined &&
          positionTickUpper !== undefined &&
          (() => {
            const startIdx = display.findIndex(
              (b) => b.tick >= positionTickLower,
            );
            const endIdx =
              display.length -
              1 -
              [...display]
                .reverse()
                .findIndex((b) => b.tick <= positionTickUpper);
            if (startIdx >= 0 && endIdx >= startIdx) {
              const x1 = ml + startIdx * (barW + barGap);
              const x2 = ml + (endIdx + 1) * (barW + barGap) - barGap;
              return (
                <rect
                  x={x1}
                  y={mt}
                  width={x2 - x1}
                  height={chartH}
                  fill={`${ACCENT}0.06)`}
                  stroke={`${ACCENT}0.15)`}
                  strokeWidth={0.5}
                />
              );
            }
            return null;
          })()}

        {/* Bars */}
        {display.map((bucket, i) => {
          const barH = (bucket.liquidity / maxLiq) * chartH;
          const x = ml + i * (barW + barGap);
          const y = mt + chartH - barH;
          const isInRange =
            positionTickLower !== undefined &&
            positionTickUpper !== undefined &&
            bucket.tick >= positionTickLower &&
            bucket.tick <= positionTickUpper;
          const isCurrent = i === currentIdx;
          const isHov = hovered === i;

          let fill: string;
          let opacity: number;
          if (isCurrent) {
            fill = "url(#liq-bar-current)";
            opacity = 1;
          } else if (isInRange) {
            fill = "url(#liq-bar-in-range)";
            opacity = 0.8;
          } else {
            fill = "url(#liq-bar-default)";
            opacity = 0.6;
          }

          if (isHov) opacity = 1;

          return (
            <rect
              key={bucket.tick}
              x={x}
              y={y}
              width={barW}
              height={Math.max(0.5, barH)}
              fill={fill}
              opacity={opacity}
              rx={1}
              onMouseEnter={() => {
                setHovered(i);
              }}
              style={{ cursor: "crosshair" }}
            />
          );
        })}

        {/* Current tick reference line */}
        {currentIdx >= 0 && (
          <>
            <line
              x1={ml + currentIdx * (barW + barGap) + barW / 2}
              y1={mt - 2}
              x2={ml + currentIdx * (barW + barGap) + barW / 2}
              y2={mt + chartH + 2}
              stroke={`${GOLD}0.6)`}
              strokeWidth={1}
              strokeDasharray="3 2"
            />
            <text
              x={ml + currentIdx * (barW + barGap) + barW / 2}
              y={mt - 1}
              textAnchor="middle"
              fontSize={8}
              fontFamily="monospace"
              fontWeight={600}
              fill={`${GOLD}0.7)`}
              letterSpacing="0.05em"
            >
              CURRENT
            </text>
          </>
        )}

        {/* Bottom axis line */}
        <line
          x1={ml}
          y1={mt + chartH}
          x2={w - mr}
          y2={mt + chartH}
          stroke={`${GOLD}0.12)`}
          strokeWidth={0.5}
        />
      </svg>

      {/* X axis labels — HTML to avoid preserveAspectRatio="none" distortion */}
      {[
        0,
        Math.floor(n / 4),
        Math.floor(n / 2),
        Math.floor((3 * n) / 4),
        n - 1,
      ].map((idx) => {
        const bucket = display[idx];
        if (!bucket) return null;
        return (
          <span
            key={bucket.tick}
            className="pointer-events-none absolute font-mono"
            style={{
              left: `${String(((ml + idx * (barW + barGap) + barW / 2) / w) * 100)}%`,
              bottom: 2,
              transform: "translateX(-50%)",
              fontSize: 9,
              color: "rgba(201,168,76,0.4)",
            }}
          >
            {formatXLabel(bucket.tick)}
          </span>
        );
      })}

      {/* Hover tooltip */}
      {hoveredBucket && hovered !== null && (
        <div
          className="pointer-events-none absolute z-10 rounded border border-[rgba(201,168,76,0.2)] bg-[rgba(13,17,23,0.95)] px-2.5 py-1.5 font-mono text-[10px] shadow-lg backdrop-blur-sm"
          style={{
            left: `${String(((ml + hovered * (barW + barGap) + barW / 2) / w) * 100)}%`,
            top: mt - 4,
            transform: "translateX(-50%) translateY(-100%)",
          }}
        >
          <div className="text-[var(--color-gold)]">
            Tick {hoveredBucket.tick.toLocaleString()}
          </div>
          {hasPriceData && (
            <div className="text-[var(--color-text-dim)]">
              Price: {tickToPrice(hoveredBucket.tick, decimals0, decimals1)}{" "}
              {token1Symbol} per {token0Symbol}
            </div>
          )}
          <div className="text-[var(--color-text-dim)]">
            Liq: {hoveredBucket.liquidity.toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}
